import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useCompany } from '../../contexts/CompanyContext';
import api from '../../api';
import { ReactComponent as OperatorIcon } from '../../icons/waiting-user.svg';
import { ReactComponent as AdminIcon } from '../../icons/category-admin.svg';
import './operator-rota.scss';

type OperatorRotaShift = '0-8' | '8-16' | '16-21' | '21-24';

const OPERATOR_ROTA_SHIFTS: OperatorRotaShift[] = [
  '8-16',
  '16-21',
  '21-24',
  '0-8',
];

const SHIFT_LABELS: Record<OperatorRotaShift, string> = {
  '0-8': '00:00 - 08:00',
  '8-16': '08:00 - 16:00',
  '16-21': '16:00 - 21:00',
  '21-24': '21:00 - 00:00',
};

interface RotaAssignment {
  date: string;
  shift: OperatorRotaShift;
  operatorPresent: boolean;
  adminPresent: boolean;
}

interface RotaDate {
  date: string;
  shifts: Record<OperatorRotaShift, RotaAssignment>;
}

interface GetRotaResponse {
  success: boolean;
  startDate: string;
  days: number;
  dates: RotaDate[];
  unassignedOperatorShifts: { date: string; shift: OperatorRotaShift }[];
  unassignedAdminShifts: { date: string; shift: OperatorRotaShift }[];
  error?: string;
}

interface SaveRotaResponse {
  success: boolean;
  count: number;
  error?: string;
}

interface RotaSettingsResponse {
  success: boolean;
  busyOpenTaskThreshold: number;
  error?: string;
}

interface AssignmentMap {
  [key: string]: {
    operatorPresent: boolean;
    adminPresent: boolean;
  };
}

const OperatorRota: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { isOpsInternalUser } = useCompany();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const [startDate, setStartDate] = useState<string>('');
  const [days, setDays] = useState<number>(28);
  const [dates, setDates] = useState<GetRotaResponse['dates']>([]);
  const [assignments, setAssignments] = useState<AssignmentMap>({});
  const [initialAssignments, setInitialAssignments] = useState<AssignmentMap>(
    {},
  );

  const [busyThreshold, setBusyThreshold] = useState<number | ''>('');
  const [savedBusyThreshold, setSavedBusyThreshold] = useState<number | null>(
    null,
  );
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [thresholdMessage, setThresholdMessage] = useState<string | null>(null);

  const fetchRota = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSaveMessage(null);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();
      if (startDate) params.append('start', startDate);
      params.append('days', String(days));

      const res = await api.get<GetRotaResponse>(
        `/admin/operator-rota?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.success) {
        setDates(res.data.dates);
        const map: AssignmentMap = {};
        for (const d of res.data.dates) {
          for (const shift of OPERATOR_ROTA_SHIFTS) {
            const assignment = d.shifts[shift];
            map[`${assignment.date}:${assignment.shift}`] = {
              operatorPresent: assignment.operatorPresent,
              adminPresent: assignment.adminPresent,
            };
          }
        }
        setAssignments(map);
        setInitialAssignments(map);
      } else {
        setError(res.data.error || 'Failed to load rota.');
      }
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      setError(
        error?.response?.data?.error || error.message || 'Failed to load rota.',
      );
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, startDate, days]);

  useEffect(() => {
    if (!isOpsInternalUser) return;
    fetchRota();
  }, [fetchRota, isOpsInternalUser]);

  useEffect(() => {
    if (!isOpsInternalUser) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<RotaSettingsResponse>(
          '/admin/operator-rota/settings',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) {
          setBusyThreshold(res.data.busyOpenTaskThreshold);
          setSavedBusyThreshold(res.data.busyOpenTaskThreshold);
        }
      } catch {
        // Non-fatal: the rota itself still works without the setting.
      }
    })();
  }, [getAccessTokenSilently, isOpsInternalUser]);

  const handleSaveThreshold = async () => {
    if (busyThreshold === '' || busyThreshold < 1) {
      setThresholdMessage('Enter a whole number of 1 or more.');
      return;
    }
    setSavingThreshold(true);
    setThresholdMessage(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post<RotaSettingsResponse>(
        '/admin/operator-rota/settings',
        { busyOpenTaskThreshold: busyThreshold },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setSavedBusyThreshold(res.data.busyOpenTaskThreshold);
        setThresholdMessage('Saved.');
      } else {
        setThresholdMessage(res.data.error || 'Failed to save threshold.');
      }
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      setThresholdMessage(
        error?.response?.data?.error ||
          error.message ||
          'Failed to save threshold.',
      );
    } finally {
      setSavingThreshold(false);
    }
  };

  const unassignedOperator = useMemo(() => {
    return dates.flatMap((d) =>
      OPERATOR_ROTA_SHIFTS.filter(
        (s) => !assignments[`${d.date}:${s}`]?.operatorPresent,
      ).map((s) => ({ date: d.date, shift: s })),
    );
  }, [dates, assignments]);

  const unassignedAdmin = useMemo(() => {
    return dates.flatMap((d) =>
      OPERATOR_ROTA_SHIFTS.filter(
        (s) => !assignments[`${d.date}:${s}`]?.adminPresent,
      ).map((s) => ({ date: d.date, shift: s })),
    );
  }, [dates, assignments]);

  const handleAssignmentChange = (
    date: string,
    shift: OperatorRotaShift,
    field: 'operatorPresent' | 'adminPresent',
    value: boolean,
  ) => {
    setAssignments((prev) => ({
      ...prev,
      [`${date}:${shift}`]: {
        ...prev[`${date}:${shift}`],
        [field]: value,
      },
    }));
    setSaveMessage(null);
  };

  const handleFillColumn = (
    shift: OperatorRotaShift,
    field: 'operatorPresent' | 'adminPresent',
  ) => {
    setAssignments((prev) => {
      const next = { ...prev };
      for (const d of dates) {
        const key = `${d.date}:${shift}`;
        next[key] = { ...prev[key], [field]: true };
      }
      return next;
    });
    setSaveMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      const token = await getAccessTokenSilently();

      const changedAssignments = Object.entries(assignments).filter(
        ([key, value]) => {
          const initial = initialAssignments[key] || {
            operatorPresent: false,
            adminPresent: false,
          };
          return (
            value.operatorPresent !== initial.operatorPresent ||
            value.adminPresent !== initial.adminPresent
          );
        },
      );

      if (changedAssignments.length === 0) {
        setSaveMessage('No changes to save.');
        setSaving(false);
        return;
      }

      const payload = {
        assignments: changedAssignments.map(([key, value]) => {
          const [date, shift] = key.split(':') as [string, OperatorRotaShift];
          return {
            date,
            shift,
            operatorPresent: value.operatorPresent,
            adminPresent: value.adminPresent,
          };
        }),
      };

      const res = await api.post<SaveRotaResponse>(
        '/admin/operator-rota',
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.success) {
        setSaveMessage(`Saved ${res.data.count} assignments.`);
        setInitialAssignments({ ...assignments });
      } else {
        setError(res.data.error || 'Failed to save rota.');
      }
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      setError(
        error?.response?.data?.error || error.message || 'Failed to save rota.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpsInternalUser) {
    return (
      <div className='operator-rota operator-rota--unauthorized'>
        <h1>Operator Rota</h1>
        <p>You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div className='operator-rota'>
      <header className='operator-rota__header'>
        <h1>Operator Rota</h1>
        <div className='operator-rota__controls'>
          <label className='operator-rota__label'>
            Start
            <input
              type='date'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className='operator-rota__date-input'
            />
          </label>
          <label className='operator-rota__label'>
            Days
            <input
              type='number'
              min={1}
              max={90}
              value={days}
              onChange={(e) =>
                setDays(Math.min(90, Math.max(1, Number(e.target.value))))
              }
              className='operator-rota__days-input'
            />
          </label>
          <button
            onClick={fetchRota}
            disabled={loading}
            className='operator-rota__button operator-rota__button--secondary'
          >
            {loading ? 'Loading…' : 'Load'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className='operator-rota__button operator-rota__button--primary'
          >
            {saving ? 'Saving…' : 'Save Rota'}
          </button>
        </div>
      </header>

      {error && <div className='operator-rota__error'>{error}</div>}
      {saveMessage && (
        <div className='operator-rota__success'>{saveMessage}</div>
      )}

      <div className='operator-rota__settings'>
        <label className='operator-rota__label'>
          Busy threshold (open tasks)
          <input
            type='number'
            min={1}
            max={1000}
            value={busyThreshold}
            onChange={(e) => {
              const v = e.target.value;
              setBusyThreshold(v === '' ? '' : Math.max(1, Number(v)));
              setThresholdMessage(null);
            }}
            className='operator-rota__days-input'
          />
        </label>
        <button
          onClick={handleSaveThreshold}
          disabled={savingThreshold || busyThreshold === savedBusyThreshold}
          className='operator-rota__button operator-rota__button--primary'
        >
          {savingThreshold ? 'Saving…' : 'Save Threshold'}
        </button>
        <span className='operator-rota__settings-hint'>
          Staffing reads as busy when this many tasks are open
          {thresholdMessage ? ` — ${thresholdMessage}` : ''}
        </span>
      </div>

      <div className='operator-rota__summary'>
        <div
          className={`operator-rota__summary-item ${unassignedOperator.length > 0 ? 'operator-rota__summary-item--warning' : ''}`}
        >
          <span className='operator-rota__summary-count'>
            {unassignedOperator.length}
          </span>
          <span>shifts without an operator</span>
        </div>
        <div
          className={`operator-rota__summary-item ${unassignedAdmin.length > 0 ? 'operator-rota__summary-item--warning' : ''}`}
        >
          <span className='operator-rota__summary-count'>
            {unassignedAdmin.length}
          </span>
          <span>shifts without an admin</span>
        </div>
      </div>

      {dates.length > 0 && (
        <div className='operator-rota__table-container'>
          <table className='operator-rota__table'>
            <thead>
              <tr>
                <th>Date</th>
                {OPERATOR_ROTA_SHIFTS.map((s) => (
                  <th key={s}>
                    <div className='operator-rota__shift-header'>
                      <span>{SHIFT_LABELS[s]}</span>
                      <div className='operator-rota__shift-actions'>
                        <button
                          type='button'
                          className='operator-rota__shift-action operator-rota__shift-action--operator'
                          title='Mark Operator present for all visible dates in this column'
                          aria-label={`Mark Operator present for all visible ${SHIFT_LABELS[s]} shifts`}
                          onClick={() => handleFillColumn(s, 'operatorPresent')}
                        >
                          <OperatorIcon />
                        </button>
                        <button
                          type='button'
                          className='operator-rota__shift-action operator-rota__shift-action--admin'
                          title='Mark Admin present for all visible dates in this column'
                          aria-label={`Mark Admin present for all visible ${SHIFT_LABELS[s]} shifts`}
                          onClick={() => handleFillColumn(s, 'adminPresent')}
                        >
                          <AdminIcon />
                        </button>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((d) => (
                <tr key={d.date}>
                  <td className='operator-rota__date-cell'>
                    {new Date(d.date + 'T00:00:00').toLocaleDateString(
                      'en-GB',
                      {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      },
                    )}
                  </td>
                  {OPERATOR_ROTA_SHIFTS.map((shift) => {
                    const key = `${d.date}:${shift}`;
                    const value = assignments[key] || {
                      operatorPresent: false,
                      adminPresent: false,
                    };
                    const missingOperator = !value.operatorPresent;
                    const missingAdmin = !value.adminPresent;

                    return (
                      <td
                        key={shift}
                        className={`operator-rota__shift-cell ${missingOperator ? 'operator-rota__shift-cell--missing-operator' : ''} ${missingAdmin ? 'operator-rota__shift-cell--missing-admin' : ''}`}
                      >
                        <label className='operator-rota__field'>
                          <input
                            type='checkbox'
                            checked={value.operatorPresent}
                            onChange={(e) =>
                              handleAssignmentChange(
                                d.date,
                                shift,
                                'operatorPresent',
                                e.target.checked,
                              )
                            }
                          />
                          Operator present
                        </label>
                        <label className='operator-rota__field'>
                          <input
                            type='checkbox'
                            checked={value.adminPresent}
                            onChange={(e) =>
                              handleAssignmentChange(
                                d.date,
                                shift,
                                'adminPresent',
                                e.target.checked,
                              )
                            }
                          />
                          Admin present
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dates.length === 0 && !loading && <p>No rota data to display.</p>}
    </div>
  );
};

export default OperatorRota;
